import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { patrolHipsSpans } from "../../../../../http/patrol/types/patrolSettings";
import { AdminPatrolSupport, UpsertAdminPatrolSupportBody } from "../../../../../http/patrol/types/patrolSupports";
import { AdminPatrolSystem } from "../../../../../http/patrol/types/patrolSystems";
import { usePatrolSupportsApi } from "../../../../../http/patrol/usePatrolSupports";
import { usePatrolSystemsApi } from "../../../../../http/patrol/usePatrolSystems";
import { usePatrolSteelVersionsApi } from "../../../../../http/patrol/useSteelVersion";
import { AdminCommonSteelVersion } from "../../../../../http/types/commonSteelVersions";
import { AdminItem, SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";

type supportTypeValues = "terminal-curve" | "intermediate" | "both";

type supportType = {
  value: supportTypeValues;
  title: string;
};

interface SupportFormBody {
  item: AdminItem | null;
  system: AdminPatrolSystem | null;
  supportMaterial: AdminCommonSteelVersion | null;
  maxSeamsSpan: number | null;
  maxInsulationThickness: number | null;
  hipsSpans: number[];
  flatRoofCompatible: boolean;
  type: supportTypeValues;
  visibility: VisibilityScopeValue;
}

interface SupportDetailsProps {
  support?: AdminPatrolSupport;
  disabled?: boolean;
  onClose: () => void;
}

const SupportForm: FC<SupportDetailsProps> = ({ support, disabled, onClose }: SupportDetailsProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const aborter = useRef(new AbortController());
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);

  const itemsApi = useItemsApi();
  const patrolSystemsApi = usePatrolSystemsApi();
  const supportMaterialApi = usePatrolSteelVersionsApi();
  const supportElementsApi = usePatrolSupportsApi();

  const supportMaterialResult = supportMaterialApi.useAdminMaterials({}, aborter.current.signal);
  const patrolSystemsResult = patrolSystemsApi.useAdminPatrolSystems({ limit: 100 }, aborter.current.signal);

  const onSubmit = () => async (formData: SupportFormBody) => {
    setIsSubmitting(true);

    const body: UpsertAdminPatrolSupportBody = {
      systemId: patrolSystemId ?? formData.system?.id!,
      itemId: formData.item?.id!,
      supportMaterialId: formData.supportMaterial?.id!,
      maxSeamsSpan: formData.maxSeamsSpan ?? undefined,
      hipsSpans: formData.hipsSpans ?? [],
      maxInsulationThickness: formData.maxInsulationThickness ?? undefined,
      flatRoofCompatible: formData.flatRoofCompatible!,
      forTerminalsAndCurves: formData.type === "terminal-curve" || formData.type === "both",
      forIntermediates: formData.type === "intermediate" || formData.type === "both",
      visibility: formData.visibility,
    };
    if (support?.item.id) {
      try {
        await supportElementsApi.updateAdminPatrolSupport(support?.item.id, body);
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      try {
        await supportElementsApi.insertAdminPatrolSupport(body);
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const extraTypes: supportType[] = [
    {
      value: "both",
      title: i18n.translation.common.terminalCurveAndIntermediate,
    },
    {
      value: "terminal-curve",
      title: i18n.translation.common.terminalAndCurve,
    },
    {
      value: "intermediate",
      title: i18n.translation.common.intermediate,
    },
  ];

  const type: supportTypeValues =
    support?.forIntermediates && support.forTerminalsAndCurves
      ? "both"
      : support?.forIntermediates
        ? "intermediate"
        : support?.forTerminalsAndCurves
          ? "terminal-curve"
          : "both";

  const defaultValues: SupportFormBody = {
    system: support?.system ?? null,
    item: support?.item ?? null,
    supportMaterial: support?.supportMaterial ?? null,
    maxSeamsSpan: support?.maxSeamsSpan ?? null,
    maxInsulationThickness: support?.maxInsulationThickness ?? null,
    hipsSpans: support?.hipsSpans ?? [],
    flatRoofCompatible: support ? support?.flatRoofCompatible : true,
    type: type,
    visibility: support?.visibility ?? "hidden",
  };

  return (
    <Form<SupportFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, setValue, watch }) => {
        const hipsSpans = watch("hipsSpans");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                    // Fetch initial data on mount
                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultOptions}
                        loadOptions={loadOptions}
                        getOptionValue={(x) => x.id}
                        getOptionLabel={(x) => x.code}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <Controller
              name="system"
              control={control}
              rules={{ required: !patrolSystemId }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.patrolSystems.singular}
                  error={fieldState.error}
                  hidden={!!patrolSystemId}
                >
                  {({ labelId }) => (
                    <CustomSelect<AdminPatrolSystem>
                      {...field}
                      inputId={labelId}
                      options={patrolSystemsResult.data?.items ?? []}
                      isDisabled={!!patrolSystemsResult.error || isSubmitting}
                      isLoading={patrolSystemsResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.type} error={fieldState.error}>
                  {({ labelId }) => (
                    <CustomSelect<supportType>
                      {...field}
                      inputId={labelId}
                      options={extraTypes}
                      isDisabled={isSubmitting}
                      isLoading={isSubmitting}
                      getOptionValue={(x) => x.value}
                      getOptionLabel={(x) => x.title}
                      value={extraTypes.find((option) => option.value === field.value) ?? null}
                      onChange={(option) => setValue("type", extraTypes.find((s) => s.value === option?.value)?.value!)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="supportMaterial"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.materials.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <CustomSelect<AdminCommonSteelVersion>
                      {...field}
                      inputId={labelId}
                      options={supportMaterialResult.data?.items ?? []}
                      isClearable
                      isDisabled={!!supportMaterialResult.error || isSubmitting}
                      isLoading={supportMaterialResult.isLoading}
                      getOptionValue={(x) => x.id}
                      getOptionLabel={(x) => x.name}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="maxSeamsSpan"
              control={control}
              rules={{
                validate: (value) => {
                  if (value && watch("maxInsulationThickness")) {
                    return i18n.translation.common.youCannotMixUpSubstructureMaxSpanAndMaxInsulationThickness;
                  }

                  return;
                },
              }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.maxSeamsSpan} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled || isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("maxSeamsSpan", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="maxInsulationThickness"
              control={control}
              rules={{
                validate: (value) => {
                  if (value && watch("maxSeamsSpan")) {
                    return i18n.translation.common.youCannotMixUpSubstructureMaxSpanAndMaxInsulationThickness;
                  }

                  return;
                },
              }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.maxInsulationThickness} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={disabled || isSubmitting}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("maxInsulationThickness", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.flatRoofCompatible} isOptional>
              {({ labelId }) => (
                <Switch
                  id={labelId}
                  checked={watch("flatRoofCompatible")}
                  disabled={disabled}
                  onChange={(checked) => setValue("flatRoofCompatible", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.hipsSpans}>
              <MultiCheckbox
                onUpdate={(id, checked) => {
                  if (checked) {
                    setValue("hipsSpans", [...hipsSpans, Number(id)]);
                  } else {
                    setValue(
                      "hipsSpans",
                      hipsSpans.filter((s) => s !== Number(id)),
                    );
                  }
                }}
                values={patrolHipsSpans.map((s) => ({
                  id: `${s.value}`,
                  title: `${s.value}`,
                  selected: hipsSpans.includes(s.value),
                }))}
              />
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SupportForm;
